import * as _ from 'lodash';
import { useCallback, useState } from 'react';
import { Alert } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';

import { toOrgAndLocators } from '../../../shared/InternalTools/buildLocators';
import { useDialogContext } from '../../contexts/DialogContext';
import useAsyncStatus from '../../hooks/useAsyncStatus';
import { useIsOnPrem } from '../../hooks/useIsOnPrem';
import { post } from '../../redux/api';
import { LabeledCheckbox, LabeledTextArea, LabeledTextInput } from '../generic';
import Accordion from '../generic/Accordion';
import { InlineLoaderButton } from '../generic/Button';
import Card from '../generic/Card/Card';
import Flex from '../generic/Flex';
import Text from '../generic/Text';
import type { BuildLocatorOpts, BuildLocatorResponse } from '../../../shared/InternalTools/buildLocators';
import type { FailedFetchResponse } from '../../helpers/errors';

export const defaultProps = {
  isResolved: false,
  isRebuild: true,
  isLicenseOnlyRebuild: true,
  defaultOrganization: 1,
  locatorsToRebuildRawContent: '',
};

const confirmMsg = (opts: BuildLocatorOpts) => {
  const orgAndLocator = toOrgAndLocators(opts);
  const uniqueOrgIds = _.uniq(orgAndLocator.map(o => o.org));
  const totalBuilds = orgAndLocator.length;

  return (
    <div>
      <h5>
        <p>
          <code>buildManager.queue</code> will be called with following options
          for provided <code>({ totalBuilds.toString() }) locators</code>.
        </p>
      </h5>
      <pre>{JSON.stringify({
        rebuild: opts.isRebuild,
        isLicenseOnlyRebuild: opts.isLicenseOnlyRebuild,
        resolved: opts.isResolved,
      }, null, 2) }
      </pre>
      <p>
        Following are unique OrganizationIds found in locators to build:
        <pre>{JSON.stringify(uniqueOrgIds, null, 2) }</pre>
      </p>
      <hr />
      <p>
        <b>
          Please beware that there is no undo for this action,
          and may have consequential impact on FOSSAs queue latency and overall performance.
        </b>
      </p>
      <p>
        Only perform this action, if you are confident in what
        you are doing.
      </p>
    </div>
  );
};

const BuildLocatorsComponent: VoidFunctionComponent = () => {
  const [opts, setOpts] = useState<BuildLocatorOpts>(defaultProps);

  const [alert, setAlert] = useState<string | null>(null);
  const { onConfirm } = useDialogContext();
  const { status, onPending, onSuccess, onError } = useAsyncStatus();

  const isOnPrem = useIsOnPrem();
  const maxLocators = isOnPrem ? 50 : 2500;

  const submitLocatorsToBuild = useCallback(async () => {
    onPending();
    try {
      const { data } = await post<BuildLocatorOpts, BuildLocatorResponse>(`/api/internal-tools/buildLocators`, opts);
      onSuccess();
      setAlert(data?.msg || 'Did not receive recognizable response from server');
      setOpts({ ...opts, locatorsToRebuildRawContent: '' });
    } catch (err) {
      onError(err as FailedFetchResponse);
    }
  }, [opts, onPending, onSuccess, onError]);

  return (
    <Card isFullWidth padding="medium">
      <Card.Content>
        <Accordion id="bulk-build-locators" isInitiallyOpen={false}>
          <Accordion.Target>Bulk Build Locators</Accordion.Target>
          <Accordion.Body>
            <Flex justify="left" direction="column" align="left">
              <Text.Paragraph>
                This tool will queue a build for all of the locators in the list below.
                {!isOnPrem &&
                <Text.Text>
                  The builds will be queued on the maintenance-builds queue, which probably already has jobs on it,
                  so it may take a while for these builds to get started
                </Text.Text>}
              </Text.Paragraph>
              <Flex justify="left" direction="column" align="left">
                <LabeledCheckbox
                  label="isResolved? (Check this if all of the locators are resolved. If you check this with unresolved locators, then they just won't get queued. Useful if you are queuing up a bunch of github.com locators and want to avoid hitting the GitHub API and getting rate limited)"
                  isChecked={opts.isResolved}
                  onChange={e => setOpts({ ...opts, isResolved: e.target.checked })} />
                <LabeledCheckbox
                  label="isRebuild? (Force the locator to be rebuilt. If unchecked, the locator will only be rebuilt if it has never been rebuilt or it is stale)"
                  isChecked={opts.isRebuild}
                  onChange={e => setOpts({ ...opts, isRebuild: e.target.checked })} />
                <LabeledCheckbox
                  label="isLicenseOnlyRebuild? (Only rebuild license information for this locator. This will leave the list of dependencies untouched and just re-run a license-scan on the locator)"
                  isChecked={opts.isLicenseOnlyRebuild}
                  onChange={e => setOpts({ ...opts, isLicenseOnlyRebuild: e.target.checked })} />
                <LabeledTextInput
                  label="Default orgID (e.g. '1')"
                  value={opts.defaultOrganization.toString()}
                  onChange={e => setOpts({ ...opts, defaultOrganization: parseInt(e, 10) || opts.defaultOrganization })}
            />
                <div style={{ width: '100%' }}>
                  <LabeledTextArea
                    label={`(Max of ${maxLocators}) Locators separated by line-break (format: 'orgId, locator' or 'locator' (which will use the default orgID)):`}
                    value={opts.locatorsToRebuildRawContent}
                    placeholder="npm+react$16.0.0&#10;10, npm+colors$1.0.0"
                    onChange={c => setOpts({ ...opts, locatorsToRebuildRawContent: c })}
                    isResizable />
                </div>
              </Flex>
            </Flex>
            <Flex justify="right">
              <InlineLoaderButton
                onClick={() => {
                  onConfirm({
                    title: 'Are you sure?',
                    body: confirmMsg(opts),
                    onConfirm: submitLocatorsToBuild,
                  });
                }}
                isLoading={status.loading}
                isDisabled={
            status.loading ||
            toOrgAndLocators(opts).length <= 0 ||
            toOrgAndLocators(opts).length > maxLocators
          }>
                Submit
              </InlineLoaderButton>
            </Flex>
            <div className="margin-top-lg">
              {alert &&
              <Alert
                bsStyle="success"
                onDismiss={() => {
                  setAlert(null);
                }}>
                {alert}
              </Alert>}
            </div>
          </Accordion.Body>
        </Accordion>
      </Card.Content>
    </Card>
  );
};

export default BuildLocatorsComponent;
